import {Modal} from '@base-components/modal/modal'
import type {JSX, ReactNode} from 'react'
import {Spinner} from '../loading/spinner'
import * as Style from './modal.styled'
import {type ModalProps} from './notification-modal'

interface ActionModalProps extends ModalProps {
    onConfirm?: () => void
    closeOnConfirm?: boolean
    isLoading?: boolean
    isNegative?: boolean
    errorText?: string
    cancelBtnText?: string
    // Actions button provided by
    // the component that uses this modal,
    // if this is provided, we won't use the
    // onConfirm callback.
    actions?: ReactNode
    closeOnError?: boolean
}

/**
 * A modal component for user to act on. Contains a cancel and an action button.
 * Side effect can be executed by passing a callback to onClose and onConfirm parameters.
 * Pass in custom string to confirmBtnText, message and title to format the
 * modal accordingly.
 * This modal does not manage its own state and instead relies on its ancestor
 * to manage its state through setting `isOpen` property. It tracks `isOpen`
 * property and will open and close the modal accordingly.
 *
 * @param actionModal - Properties of modal
 * @param actionModal.isOpen - State of display for modal
 * @param actionModal.onClose - Callback function when closing the modal. Needs
 * to change state of `actionModal.isOpen` to false.
 * @param actionModal.onConfirm - Callback function for action button. Can
 * execute side effect here. The callback here will be followed by call to
 * `actionModal.onClose`
 * @param actionModal.confirmBtnText - Text on confirm action button
 * @param actionModal.message - Text for body of modal
 * @param actionModal.title - Text for header of modal
 * @param actionModal.isLoading - Will show spinner in the confirm button if true
 * @param actionModal.isNegative - Cancel button will be solid if true and confirm button will be outlined
 * @param actionModal.closeOnConfirm - Will close modal after confirm button is clicked if true
 * @param actionModal.errorText - Text to display as error message, will only display if its a valid string
 *
 */
export function ActionModal({
    isOpen,
    onClose,
    cancelBtnText = 'No, cancel',
    onConfirm,
    confirmBtnText = 'Yes, submit',
    message = 'Are you sure you want to make this action?',
    title = 'Action',
    isLoading = false,
    closeOnConfirm = true,
    isNegative = false,
    errorText,
    actions,
    // NOTE: default is true to retain existing behaviour
    closeOnError = true,
}: ActionModalProps): JSX.Element {
    const handleSubmitModal = () => {
        if (onConfirm) {
            onConfirm()
        }

        if (onClose && closeOnConfirm) {
            if (closeOnError) {
                onClose()
                return
            }

            if (errorText === undefined || errorText === '') {
                onClose()
            }
        }
    }

    const onOpenChangeHandler = (value: boolean) => {
        if (!value) {
            onClose?.()
        }
    }

    return (
        <Modal
            label="action modal"
            isOpen={isOpen}
            isKeyboardDismissDisabled={false}
            onOpenChange={onOpenChangeHandler}
        >
            <Style.Container>
                <Style.TopDiv>
                    <Style.Title>{title}</Style.Title>
                    <Style.Message>{message}</Style.Message>
                </Style.TopDiv>

                <Style.ActionDiv>
                    {actions ? (
                        actions
                    ) : isNegative ? (
                        <>
                            <Style.ConfirmBtn disabled={isLoading} onClick={onClose}>
                                No, cancel
                            </Style.ConfirmBtn>
                            <Style.CancelBtn onClick={handleSubmitModal}>
                                {isLoading && <Spinner />}
                                {confirmBtnText}
                            </Style.CancelBtn>
                        </>
                    ) : (
                        <>
                            <Style.CancelBtn disabled={isLoading} onClick={onClose}>
                                {cancelBtnText}
                            </Style.CancelBtn>
                            <Style.ConfirmBtn onClick={handleSubmitModal}>
                                {isLoading && <Spinner />}
                                {confirmBtnText}
                            </Style.ConfirmBtn>
                        </>
                    )}
                </Style.ActionDiv>

                {errorText && <Style.ErrorDiv>{errorText}</Style.ErrorDiv>}
            </Style.Container>
        </Modal>
    )
}
